/* eslint-disable max-lines */
/**
 * ManagerReviewV2View — Manager Review tool, v2 (Nov 2026 cycle; pilot until cutover)
 *
 * Manages onboarding steps 0–5 and the full assessment state for step 5.
 * Steps:
 *   0 = Welcome screen
 *   1 = Intro slides (4 slides explaining the 4 assessment sections)
 *   2 = Select Employee
 *   3 = Framework Preview
 *   4 = Before You Start (StepReady)
 *   5 = Assessment (tabs + sidebars)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import AccessDeniedScreen from '../../../components/AccessDeniedScreen';
import { useAuth } from '../../../contexts/AuthContext';
import { C, FINN_VALUES, RATING_SCALE, BIAS_CHECKS } from '../data/managerReviewData';
import {
  fetchDirectReports,
  NotAManagerError,
  PeopleDatabaseNotConfiguredError,
} from '../services/managerReviewV2Api';
import { setEmployeeNames } from '../components/manager-review-v2/domainDictionary';
import { loadDraft, saveDraft, loadAllSubmittedDrafts } from '../services/managerReviewV2DraftService';
import type {
  Employee,
  Deliverable,
  BehaviorData,
  FeedbackItem,
  LeadershipData,
  PromotionChoice,
  ActiveTab,
  ActiveSidebar,
} from '../types/managerReviewV2';

// Onboarding step components
import StepWelcome from '../components/manager-review-v2/onboarding/StepWelcome';
import StepIntro from '../components/manager-review-v2/onboarding/StepIntro';
import StepSelectEmployee from '../components/manager-review-v2/onboarding/StepSelectEmployee';
import StepFrameworkPreview from '../components/manager-review-v2/onboarding/StepFrameworkPreview';
import StepReady from '../components/manager-review-v2/onboarding/StepReady';
import { type CompletedReview } from '../components/manager-review-v2/onboarding/StepComplete';
import StepAllReviews from '../components/manager-review-v2/onboarding/StepAllReviews';

// Assessment components
import AssessmentNav from '../components/manager-review-v2/AssessmentNav';
import { missingRoundDownNote } from '../components/manager-review-v2/behaviorRating';
import { emptyLeadership, leadershipComplete, restoreLeadership, leadershipMissingInput } from '../components/manager-review-v2/leadershipValues';
import { newDeliverable, withIds } from '../components/manager-review-v2/deliverables';
import DeliveryTab from '../components/manager-review-v2/DeliveryTab';
import BehaviorsTab from '../components/manager-review-v2/BehaviorsTab';
import SummaryTab from '../components/manager-review-v2/SummaryTab';
import AIReviewTab, { type ParsedOutput } from '../components/manager-review-v2/AIReviewTab';
import FrameworkSidebar from '../components/manager-review-v2/FrameworkSidebar';
import FeedbackSidebar from '../components/manager-review-v2/FeedbackSidebar';
import PromoFrameworkSidebar from '../components/manager-review-v2/PromoFrameworkSidebar';

// Steps: 0=Welcome, 1=Intro, 2=SelectEmployee, 3=FrameworkPreview,
//        4=BeforeYouStart, 5=Assessment, 6=Complete, 7=AllReviews
type OnboardingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Bump whenever the draft JSON shape changes in a breaking way.
// restoreFromDraft migrates older records to the current shape before applying state.
const DRAFT_SCHEMA_VERSION = 2;

const emptyDeliverable = (): Deliverable => newDeliverable();

export default function ManagerReviewV2View() {
  const { user } = useAuth();
  const managerName = user?.displayName || user?.email?.split('@')[0] || '';

  // ── Direct reports (loaded from Airtable People database) ─────────────
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [isNotManager, setIsNotManager] = useState(false);

  // Fetch once on mount (user is already authenticated at this point)
  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEmployeesLoading(true);
    setEmployeesError(null);
    setIsNotManager(false);
    fetchDirectReports()
      .then(({ employees: data }) => {
        if (!cancelled) setEmployees(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof NotAManagerError) {
          setIsNotManager(true);
        } else if (err instanceof PeopleDatabaseNotConfiguredError) {
          setEmployeesError('The People database connection is not set up yet. Contact the tool admin.');
        } else {
          setEmployeesError(err instanceof Error ? err.message : 'Failed to load direct reports.');
        }
      })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Onboarding ──────────────────────────────────────────────────────────
  // Skip welcome + intro if the manager has been through onboarding before
  // Use user.email directly — App.tsx guarantees user is non-null before this
  // component renders, so the key is always a real email, never "undefined".
  const onboardingKey = `finn-mr2-onboarded-${user?.email ?? ''}`;
  const hasOnboarded = !!user?.email && typeof window !== 'undefined' && !!localStorage.getItem(onboardingKey);
  const [step, setStep] = useState<OnboardingStep>(hasOnboarded ? 2 : 0);
  const [employee, setEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    if (employees.length > 0) {
      setEmployeeNames(employees.map(e => e.name));
    }
  }, [employees]);

  // ── Feedback / self-review (gathered during onboarding, shown in sidebar) ─
  const [selfReview, setSelfReview] = useState('');
  const [perfFeedbackRaw, setPerfFeedbackRaw] = useState('');
  const [anytimeFeedbackRaw, setAnytimeFeedbackRaw] = useState('');
  const [cultureAmpMode, setCultureAmpMode] = useState<null | 'read' | 'paste'>(null);
  // feedbackItems kept for AI-prompt backward-compat; no longer editable in StepReady
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);

  // ── Assessment state ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>(0);
  const [activeSidebar, setActiveSidebar] = useState<ActiveSidebar>(null);

  const [behaviorsData, setBehaviorsData] = useState<Record<string, BehaviorData>>({});
  const [behaviorsRating, setBehaviorsRating] = useState<number | null>(null);
  const [behaviorsRatingNote, setBehaviorsRatingNote] = useState('');

  const [deliverables, setDeliverables] = useState<Deliverable[]>([emptyDeliverable()]);
  const [deliveryRating, setDeliveryRating] = useState<number | null>(null);
  const [helpAnswer, setHelpAnswer] = useState<'yes' | 'no' | null>(null);
  const [leadership, setLeadership] = useState<LeadershipData>(emptyLeadership);

  const [promotion, setPromotion] = useState<PromotionChoice | null>(null);
  const [promotionNote, setPromotionNote] = useState('');
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [overallRatingNote, setOverallRatingNote] = useState('');
  const [promoTrack, setPromoTrack] = useState<'E' | 'M'>('E');

  const [biasChecked, setBiasChecked] = useState<Record<string, boolean>>({});
  const [biasNote, setBiasNote] = useState('');

  // ── AI Review generated output (persisted across tab switches) ────────────
  const [generatedReview, setGeneratedReview] = useState<ParsedOutput | null>(null);

  // ── Draft save state ──────────────────────────────────────────────────────
  const [draftStatus, setDraftStatus] = useState<'Draft' | 'Submitted' | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const airtableSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard: block Airtable saves while a draft is being loaded to prevent
  // an empty/reset form from overwriting real saved data during the load window.
  const isDraftLoadingRef = useRef(false);

  // ── Completed reviews — persisted to localStorage so hard refresh restores state ──
  const completedReviewsKey = `finn-mr2-completed-${user?.email ?? ''}`;
  const [completedReviews, setCompletedReviews] = useState<CompletedReview[]>(() => {
    try {
      const saved = localStorage.getItem(`finn-mr2-completed-${user?.email ?? ''}`);
      return saved ? (JSON.parse(saved) as CompletedReview[]) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(completedReviewsKey, JSON.stringify(completedReviews)); } catch { /* storage full */ }
  }, [completedReviewsKey, completedReviews]);

  // Hydrate completedReviews from Airtable once the employee list is ready.
  // localStorage gives an instant render; Airtable provides cross-device accuracy.
  useEffect(() => {
    if (employees.length === 0) return;
    let cancelled = false;

    loadAllSubmittedDrafts().then(remoteDrafts => {
      if (cancelled || remoteDrafts.length === 0) return;

      const isoToDisplay = (iso: string | null): string => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
      };

      const reconstructed: CompletedReview[] = [];
      for (const draft of remoteDrafts) {
        if (!draft.employeeId || !draft.draftJson) continue;
        try {
          const parsed = JSON.parse(draft.draftJson);
          // Match against loaded employee list; fall back to a minimal object so
          // previously-reviewed employees who are no longer in scope still appear.
          const emp = employees.find(e => e.id === draft.employeeId) ?? {
            id: draft.employeeId,
            name: draft.employeeName ?? draft.employeeId,
            email: '',
            title: '',
            level: parsed.level ?? '',
            track: '',
            role: '',
            avatar: (draft.employeeName ?? '?').split(' ').map((n: string) => n[0] ?? '').join('').slice(0, 2).toUpperCase(),
          };
          reconstructed.push({
            employee: emp,
            level: emp.level,
            deliveryRating: parsed.deliveryRating ?? null,
            behaviorsRating: parsed.behaviorsRating ?? null,
            overallRating: parsed.overallRating ?? null,
            promotion: parsed.promotion ?? null,
            completedAt: isoToDisplay(draft.lastSaved),
          });
        } catch { continue; }
      }

      if (reconstructed.length > 0) {
        setCompletedReviews(reconstructed);
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const [lastCompleted, setLastCompleted] = useState<{ managerFirst: string; employeeFirst: string } | null>(null);

  // ── Scroll to top on tab change ───────────────────────────────────────────
  // FINN Spaces uses a <main> overflow-auto container, not the window
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'instant' });
    else window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab]);

  // ── Draft helpers ─────────────────────────────────────────────────────────

  const getDraftPayload = useCallback(() => ({
    schemaVersion: DRAFT_SCHEMA_VERSION,
    activeTab,
    behaviorsData,
    behaviorsRating,
    behaviorsRatingNote,
    deliverables,
    deliveryRating,
    helpAnswer,
    leadership,
    promotion,
    promotionNote,
    overallRating,
    overallRatingNote,
    promoTrack,
    biasChecked,
    biasNote,
    selfReview,
    perfFeedbackRaw,
    anytimeFeedbackRaw,
    feedbackItems,
    generatedReview,
    cultureAmpMode,
  }), [activeTab, behaviorsData, behaviorsRating, behaviorsRatingNote, deliverables,
    deliveryRating, helpAnswer, leadership, promotion, promotionNote, overallRating,
    overallRatingNote, promoTrack, biasChecked, biasNote, selfReview,
    perfFeedbackRaw, anytimeFeedbackRaw, feedbackItems, generatedReview, cultureAmpMode]);

  const restoreFromDraft = useCallback((json: string, submitted = false) => {
    try {
      const s = JSON.parse(json);
      const version = typeof s.schemaVersion === 'number' ? s.schemaVersion : 1;

      // ── Schema migrations ────────────────────────────────────────────────
      // v1 → v2: behaviorsData.observed was not always present; strengthsText /
      // improvementsText may be missing on older BehaviorData entries.
      // deliverables may be missing wellDone/improvement (renamed fields).
      if (version < 2) {
        if (s.behaviorsData && typeof s.behaviorsData === 'object') {
          for (const key of Object.keys(s.behaviorsData)) {
            const bd = s.behaviorsData[key] as Record<string, unknown>;
            bd.observed = (bd.observed && typeof bd.observed === 'object') ? bd.observed : {};
            bd.strengthsText = typeof bd.strengthsText === 'string' ? bd.strengthsText
              : Array.isArray(bd.strengths) ? (bd.strengths as string[]).join('\n') : '';
            bd.improvementsText = typeof bd.improvementsText === 'string' ? bd.improvementsText
              : Array.isArray(bd.improvements) ? (bd.improvements as string[]).join('\n') : '';
            bd.rating = typeof bd.rating === 'number' ? bd.rating : null;
          }
        }
        if (Array.isArray(s.deliverables)) {
          s.deliverables = (s.deliverables as Record<string, unknown>[]).map(d => ({
            ...d,
            rating: typeof d.rating === 'number' ? d.rating : null,
            wellDone: typeof d.wellDone === 'string' ? d.wellDone : '',
            improvement: typeof d.improvement === 'string' ? d.improvement : '',
            competencies: Array.isArray(d.competencies) ? d.competencies : [],
          }));
        }
      }
      // ── End migrations ───────────────────────────────────────────────────

      if (s.activeTab != null) setActiveTab(s.activeTab);
      if (s.behaviorsData) setBehaviorsData(s.behaviorsData);
      if (s.behaviorsRating != null) setBehaviorsRating(s.behaviorsRating);
      if (s.behaviorsRatingNote != null) setBehaviorsRatingNote(s.behaviorsRatingNote);
      if (Array.isArray(s.deliverables)) setDeliverables(withIds(s.deliverables));
      if (s.deliveryRating != null) setDeliveryRating(s.deliveryRating);
      if (s.helpAnswer != null) setHelpAnswer(s.helpAnswer);
      if (s.leadership && typeof s.leadership === 'object') setLeadership(restoreLeadership(s.leadership, submitted));
      if (s.promotion != null) setPromotion(s.promotion);
      if (s.promotionNote != null) setPromotionNote(s.promotionNote);
      if (s.overallRating != null) setOverallRating(s.overallRating);
      if (s.overallRatingNote != null) setOverallRatingNote(s.overallRatingNote);
      if (s.promoTrack) setPromoTrack(s.promoTrack);
      if (s.biasChecked) setBiasChecked(s.biasChecked);
      if (s.biasNote != null) setBiasNote(s.biasNote);
      if (s.selfReview != null) setSelfReview(s.selfReview);
      if (s.perfFeedbackRaw != null) setPerfFeedbackRaw(s.perfFeedbackRaw);
      if (s.anytimeFeedbackRaw != null) setAnytimeFeedbackRaw(s.anytimeFeedbackRaw);
      if (s.feedbackItems) setFeedbackItems(s.feedbackItems);
      if (s.generatedReview) setGeneratedReview(s.generatedReview);
      if (s.cultureAmpMode != null) setCultureAmpMode(s.cultureAmpMode);
    } catch {
      // Corrupt draft — ignore and start fresh
    }
  }, []);

  // localStorage autosave — 800ms debounce on every state change in assessment
  useEffect(() => {
    if (step !== 5 || !employee) return;
    const lsKey = `finn-mr2-draft-${user?.email}-${employee.id}`;
    const timer = setTimeout(() => {
      try { localStorage.setItem(lsKey, JSON.stringify(getDraftPayload())); } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [step, employee, user?.email, getDraftPayload]);

  // Airtable save — 3s debounce, also fires immediately on tab switch
  // true only when Airtable confirmed the write — "Mark as Reviewed" must not complete without it.
  const saveToAirtable = useCallback(async (emp: Employee, status?: 'Draft' | 'Submitted'): Promise<boolean> => {
    if (!emp || isDraftLoadingRef.current) return false;
    setIsSaving(true);
    const result = await saveDraft({
      employeeId: emp.id,
      employeeName: emp.name,
      draftJson: JSON.stringify(getDraftPayload()),
      status: status ?? draftStatus ?? 'Draft',
    });
    setIsSaving(false);
    if (result) {
      setLastSavedAt(result.lastSaved);
      setSaveError(false);
      if (status) setDraftStatus(status);
      return true;
    }
    setSaveError(true);
    return false;
  }, [getDraftPayload, draftStatus]);

  // Trigger Airtable save when activeTab changes (immediate) or state changes (3s debounce)
  useEffect(() => {
    if (step !== 5 || !employee) return;
    if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current);
    airtableSaveTimer.current = setTimeout(() => saveToAirtable(employee), 3000);
    return () => { if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current); };
  }, [step, employee, getDraftPayload, saveToAirtable]);

  useEffect(() => {
    if (step !== 5 || !employee) return;
    if (airtableSaveTimer.current) clearTimeout(airtableSaveTimer.current);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    saveToAirtable(employee);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const selectEmployee = async (emp: Employee) => {
    isDraftLoadingRef.current = true;
    setEmployee(emp);
    // Reset to fresh state first (including feedback — will be restored from draft if available)
    setBehaviorsData({});
    setBehaviorsRating(null);
    setBehaviorsRatingNote('');
    setDeliverables([emptyDeliverable()]);
    setDeliveryRating(null);
    setPromotion(null);
    setPromotionNote('');
    setOverallRating(null);
    setOverallRatingNote('');
    setPromoTrack('E');
    setHelpAnswer(null);
    setLeadership(emptyLeadership());
    setBiasChecked({});
    setBiasNote('');
    setActiveTab(0);
    setSelfReview('');
    setPerfFeedbackRaw('');
    setAnytimeFeedbackRaw('');
    setFeedbackItems([]);
    setGeneratedReview(null);
    setDraftStatus(null);
    setLastSavedAt(null);
    setCultureAmpMode(null);

    // Sync localStorage check first — sets button text immediately so there's no flash
    const lsKey = `finn-mr2-draft-${user?.email}-${emp.id}`;
    const local = localStorage.getItem(lsKey);
    if (local) setDraftStatus('Draft');

    // Try Airtable (authoritative) — may override the localStorage status
    const remote = await loadDraft(emp.id);
    if (remote?.draftJson) {
      restoreFromDraft(remote.draftJson, remote.status === 'Submitted');
      setDraftStatus(remote.status);
      setLastSavedAt(remote.lastSaved);
      isDraftLoadingRef.current = false;
      return;
    }
    if (local) {
      restoreFromDraft(local);
      setDraftStatus('Draft');
    }
    isDraftLoadingRef.current = false;
  };

  const resetToEmployeeList = () => {
    setEmployee(null);
    setActiveTab(0);
    setActiveSidebar(null);
    setBehaviorsData({});
    setBehaviorsRating(null);
    setBehaviorsRatingNote('');
    setDeliverables([emptyDeliverable()]);
    setDeliveryRating(null);
    setPromotion(null);
    setPromotionNote('');
    setOverallRating(null);
    setOverallRatingNote('');
    setPromoTrack('E');
    setHelpAnswer(null);
    setLeadership(emptyLeadership());
    setBiasChecked({});
    setBiasNote('');
    setSelfReview('');
    setPerfFeedbackRaw('');
    setAnytimeFeedbackRaw('');
    setFeedbackItems([]);
    setGeneratedReview(null);
    setCultureAmpMode(null);
    setStep(2);
  };

  // Derived "done" flags
  const deliveryDone =
    deliverables.every((d) => d.rating !== null) && deliveryRating !== null &&
    leadershipComplete(employee, leadership);
  const behaviorsDone =
    FINN_VALUES.every((v) => behaviorsData[v.name]?.rating) &&
    behaviorsRating !== null &&
    !missingRoundDownNote(behaviorsData, behaviorsRating, behaviorsRatingNote);

  const computeSuggestedRating = (): number | null => {
    const bA = behaviorsRating ?? null;
    const dA = deliveryRating ?? null;
    if (!bA || !dA) return null;
    const avg = (bA + dA) / 2;
    const isTie = (avg * 10) % 5 === 0 && !Number.isInteger(avg);
    return isTie ? dA : Math.round(avg);
  };

  /** What still blocks the Summary, in the order the tab asks for it (shown on the locked Next button). */
  const computeSummaryMissing = (): string[] => {
    const rdP = overallRating ? RATING_SCALE.find((r) => r.v === overallRating) : null;
    const ratingEligP = rdP ? rdP.promotion : false;
    const noConsecP = employee ? !employee.previouslyPromoted : true;
    const reasoningReqP =
      promotion === 'yes' && (!ratingEligP || !noConsecP);
    const biasAllCheckedP =
      Object.values(biasChecked).filter(Boolean).length === BIAS_CHECKS.length;
    const suggestedRatingP = computeSuggestedRating();
    const deviationP =
      overallRating && suggestedRatingP && overallRating !== suggestedRatingP;
    const missing: string[] = [];
    if (missingRoundDownNote(behaviorsData, behaviorsRating, behaviorsRatingNote)) missing.push('the reason for your Behavior rating (Behaviors tab)');
    if (!overallRating) missing.push('the overall rating');
    else if (deviationP && !overallRatingNote.trim()) missing.push('the reason for your overall rating');
    if (promotion === null) missing.push('the promotion decision');
    else if (reasoningReqP && !promotionNote.trim()) missing.push('your promotion reasoning');
    if (!biasAllCheckedP) missing.push('all bias checks');
    return missing;
  };

  const summaryMissing = computeSummaryMissing();
  const summaryDone = summaryMissing.length === 0;
  // "Mark as Reviewed" needs every tab complete: all five values rated (so the below-suggestion
  // note check cannot be skipped) and Q4 answered where it applies.
  const canSubmit = summaryDone && deliveryDone && behaviorsDone;

  const nextTabLabel = ['Behaviors', 'Summary', 'AI Review', ''][activeTab];

  const nextLocked = activeTab === 2 && !summaryDone;

  // Q4 applies but is (partly) empty: say so before leaving Delivery, from the Next button and
  // the tab bar alike (Zohal, Nov 3 2026: "I don't want leaders to skip it or miss it accidentally").
  const confirmLeavingDelivery = (): boolean => {
    if (activeTab !== 0 || !employee || !leadershipMissingInput(employee, leadership)) return true;
    if (window.confirm(`You said ${employee.name.split(' ')[0]} has direct reports, but the Leadership Values question isn't filled in yet. Continue anyway?`)) return true;
    document.getElementById('leadership-values')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return false;
  };

  // ── Access gate: non-managers see a clear "not for you" screen ───────────
  if (isNotManager) {
    return (
      <AccessDeniedScreen
        title="Access denied"
        message={`The Manager Review tool is restricted to managers with active direct reports in the People database. If you think this is wrong, ask your People team to check that your email (${user?.email}) appears in the Supervisor Email column.`}
      />
    );
  }

  // ── Onboarding steps ──────────────────────────────────────────────────────
  if (step === 0) {
    return (
      <StepWelcome
        managerName={managerName}
        onNext={() => setStep(1)}
      />
    );
  }

  if (step === 1) {
    return (
      <StepIntro
        onNext={() => {
          localStorage.setItem(onboardingKey, '1');
          setStep(2);
        }}
        onBack={() => setStep(0)}
      />
    );
  }

  if (step === 2) {
    return (
      <StepSelectEmployee
        employees={employees}
        isLoading={employeesLoading}
        error={employeesError}
        selected={employee}
        hasDraft={draftStatus !== null}
        onSelect={selectEmployee}
        onNext={() => {
          if (!employee) return;
          // Skip framework + checks if a draft already exists or cultureAmpMode is set
          if (draftStatus !== null || cultureAmpMode !== null) { setStep(5); return; }
          const fwKey = `finn-mr2-fw-visited-${employee.id}`;
          const fwVisited = !!localStorage.getItem(fwKey);
          setStep(fwVisited ? 4 : 3);
        }}
        onBack={() => setStep(1)}
        completedReviews={completedReviews}
        onViewAll={() => setStep(7)}
        onViewIntro={() => setStep(1)}
        onRevisitPrep={() => setStep(3)}
      />
    );
  }

  if (step === 3) {
    return (
      <StepFrameworkPreview
        employee={employee!}
        onNext={() => {
          localStorage.setItem(`finn-mr2-fw-visited-${employee!.id}`, '1');
          setStep(4);
        }}
        onBack={() => setStep(2)}
      />
    );
  }

  if (step === 4) {
    return (
      <StepReady
        employee={employee!}
        onNext={() => setStep(5)}
        onBack={() => setStep(3)}
        selfReview={selfReview}
        setSelfReview={setSelfReview}
        perfFeedbackRaw={perfFeedbackRaw}
        setPerfFeedbackRaw={setPerfFeedbackRaw}
        anytimeFeedbackRaw={anytimeFeedbackRaw}
        setAnytimeFeedbackRaw={setAnytimeFeedbackRaw}
        cultureAmpMode={cultureAmpMode}
        setCultureAmpMode={setCultureAmpMode}
      />
    );
  }

if (step === 7) {
    return (
      <StepAllReviews
        completedReviews={completedReviews}
        lastCompleted={lastCompleted}
        onBack={() => setStep(2)}
        onStartNew={() => { setLastCompleted(null); resetToEmployeeList(); }}
      />
    );
  }

  // ── Step 5: Assessment ────────────────────────────────────────────────────
  if (!employee) return null;
  const level = employee.level;

  return (
    <div style={{ height: '100vh', background: '#1a1a1a', display: 'flex', overflow: 'hidden' }}>
      {/* Main content — shifts left when sidebar is open */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          marginRight: activeSidebar ? 420 : 0,
          transition: 'margin-right 0.25s',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <AssessmentNav
          employee={employee}
          level={level}
          activeTab={activeTab}
          setActiveTab={(tab) => { if (confirmLeavingDelivery()) setActiveTab(tab); }}
          activeSidebar={activeSidebar}
          setActiveSidebar={setActiveSidebar}
          deliveryDone={deliveryDone}
          behaviorsDone={behaviorsDone}
          summaryDone={summaryDone}
          draftStatus={draftStatus}
          lastSavedAt={lastSavedAt}
          isSaving={isSaving}
          saveError={saveError}
        />

        <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px 16px 60px' }}>
          {activeTab === 0 && (
            <DeliveryTab
              employee={employee}
              level={level}
              deliverables={deliverables}
              setDeliverables={setDeliverables}
              deliveryRating={deliveryRating}
              setDeliveryRating={setDeliveryRating}
              helpAnswer={helpAnswer}
              setHelpAnswer={setHelpAnswer}
              selfReview={selfReview}
              feedbackItems={feedbackItems}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
              leadership={leadership}
              setLeadership={setLeadership}
            />
          )}
          {activeTab === 1 && (
            <BehaviorsTab
              employee={employee}
              level={level}
              deliverables={deliverables}
              deliveryRating={deliveryRating}
              behaviorsData={behaviorsData}
              setBehaviorsData={setBehaviorsData}
              behaviorsRating={behaviorsRating}
              setBehaviorsRating={setBehaviorsRating}
              behaviorsRatingNote={behaviorsRatingNote}
              setBehaviorsRatingNote={setBehaviorsRatingNote}
              selfReview={selfReview}
              feedbackItems={feedbackItems}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
            />
          )}
          {activeTab === 2 && (
            <SummaryTab
              employee={employee}
              level={level}
              behaviorsData={behaviorsData}
              behaviorsRating={behaviorsRating}
              deliveryRating={deliveryRating}
              promotion={promotion}
              setPromotion={setPromotion}
              promotionNote={promotionNote}
              setPromotionNote={setPromotionNote}
              overallRating={overallRating}
              setOverallRating={setOverallRating}
              overallRatingNote={overallRatingNote}
              setOverallRatingNote={setOverallRatingNote}
              promoTrack={promoTrack}
              setPromoTrack={setPromoTrack}
              setActiveSidebar={setActiveSidebar}
              biasChecked={biasChecked}
              setBiasChecked={setBiasChecked}
              biasNote={biasNote}
              setBiasNote={setBiasNote}
            />
          )}
          {activeTab === 3 && (
            <AIReviewTab
              employee={employee}
              level={level}
              behaviorsData={behaviorsData}
              behaviorsRating={behaviorsRating}
              behaviorsRatingNote={behaviorsRatingNote}
              deliverables={deliverables}
              deliveryRating={deliveryRating}
              leadership={leadership}
              overallRating={overallRating}
              overallRatingNote={overallRatingNote}
              promotion={promotion}
              promotionNote={promotionNote}
              biasNote={biasNote}
              selfReview={selfReview}
              perfFeedbackRaw={perfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
              feedbackItems={feedbackItems}
              generatedReview={generatedReview}
              setGeneratedReview={setGeneratedReview}
              onNavigate={(tab, anchor) => {
                setActiveTab(tab as ActiveTab);
                if (anchor) setTimeout(() => {
                  document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 150);
              }}
              onOpenFeedback={() => setActiveSidebar('feedback')}
            />
          )}

          {/* Bottom navigation */}
          <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
            {activeTab > 0 ? (
              <button
                onClick={() => setActiveTab((t) => (t - 1) as ActiveTab)}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: 'transparent', color: '#fff',
                }}
              >
                ← Previous
              </button>
            ) : (
              <button
                onClick={() => {
                  if (window.confirm('Go back to employee selection? Your draft is saved and you can resume this review any time.')) resetToEmployeeList();
                }}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: 'transparent', color: 'rgba(255,255,255,0.6)',
                }}
              >
                ← Back to employees
              </button>
            )}

            {activeTab < 3 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <button
                onClick={() => {
                  if (nextLocked || !confirmLeavingDelivery()) return;
                  setActiveTab((t) => (t + 1) as ActiveTab);
                }}
                aria-describedby={nextLocked ? 'summary-missing' : undefined}
                style={{
                  fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                  border: 'none', cursor: nextLocked ? 'not-allowed' : 'pointer',
                  borderRadius: 6, padding: '10px 20px',
                  background: C.blue, color: '#fff',
                  opacity: nextLocked ? 0.45 : 1,
                }}
              >
                Next: {nextTabLabel} →
              </button>
              {nextLocked && (
                <div id="summary-missing" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', maxWidth: 360, textAlign: 'right' }}>
                  Still missing: {summaryMissing.join(', ')}
                </div>
              )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <button
                  onClick={async () => {
                    if (!canSubmit) return;
                    const saved = await saveToAirtable(employee, 'Submitted');
                    if (!saved) {
                      window.alert('Could not submit your review. Check your connection and try again.');
                      return;
                    }
                    const completedAt = (() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })();
                    setCompletedReviews(prev => {
                      const rest = prev.filter(r => r.employee.id !== employee.id);
                      return [...rest, { employee, level: employee.level, deliveryRating, behaviorsRating, overallRating, promotion, completedAt }];
                    });
                    setLastCompleted({ managerFirst: (managerName || '').split(' ')[0] || 'there', employeeFirst: employee.name.split(' ')[0] });
                    setStep(7);
                  }}
                  title={!canSubmit ? 'Complete Delivery, Behaviors and Summary before marking this review as done' : undefined}
                  style={{
                    fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
                    border: 'none',
                    cursor: !canSubmit ? 'not-allowed' : 'pointer',
                    borderRadius: 6, padding: '10px 20px',
                    background: '#26bd59', color: '#fff',
                    opacity: !canSubmit ? 0.45 : 1,
                  }}
                >
                  ✓ Mark as Reviewed
                </button>
                <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>
                  You can still edit any time.
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Right sidebar */}
      {activeSidebar && (
        <div
          style={{
            position: 'fixed', right: 0, top: 0, bottom: 0, width: 420,
            background: '#fff', borderLeft: `1px solid ${C.border}`,
            overflowY: 'auto', zIndex: 90, boxShadow: '-4px 0 20px rgba(0,0,0,0.06)',
          }}
        >
          {/* Sticky header */}
          <div
            style={{
              position: 'sticky', top: 0, background: '#fff',
              borderBottom: `1px solid ${C.border}`,
              padding: '14px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              zIndex: 1,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: '#0f0f0f' }}>
              {activeSidebar === 'feedback' ? '💬 Multi-Source Feedback' : '📋 Talent Framework'}
            </div>
            <button
              onClick={() => setActiveSidebar(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 18, color: '#aaa', lineHeight: 1, padding: 4,
              }}
            >
              ✕
            </button>
          </div>

          {activeSidebar === 'framework' && (
            <FrameworkSidebar employee={employee} level={level} />
          )}
          {activeSidebar === 'feedback' && (
            <FeedbackSidebar
              selfReview={selfReview}
              setSelfReview={setSelfReview}
              perfFeedbackRaw={perfFeedbackRaw}
              setPerfFeedbackRaw={setPerfFeedbackRaw}
              anytimeFeedbackRaw={anytimeFeedbackRaw}
              setAnytimeFeedbackRaw={setAnytimeFeedbackRaw}
              employeeName={employee.name}
            />
          )}
          {activeSidebar === 'promoframework' && (
            <PromoFrameworkSidebar
              employee={employee}
              level={level}
              promoTrack={promoTrack}
              setPromoTrack={setPromoTrack}
            />
          )}
        </div>
      )}
    </div>
  );
}
